import React from "react";
import { getDownloadLink } from "@utils/url";

interface HeroProps {
  title: string;
  description: string;
  buttonText: string;
  imageSrc: string;
  imageAlt: string;
  appleButtonSrc: string;
  googleButtonSrc: string;
}

/**
 * Hero component for the landing page
 *
 * Generic hero section component that displays title, description, call-to-action button,
 * and app download buttons. Receives all content as props for A/B testing flexibility.
 *
 * @param {string} title - Main headline text
 * @param {string} description - Description text below the title
 * @param {string} buttonText - Text for the call-to-action button
 * @param {string} imageSrc - Path to the hero image
 * @param {string} imageAlt - Alt text for the hero image
 * @param {string} appleButtonSrc - Path to the App Store button image
 * @param {string} googleButtonSrc - Path to the Google Play button image
 * @returns {JSX.Element} The hero section component
 */
export function Hero({
  title,
  description,
  buttonText,
  imageSrc,
  imageAlt,
  appleButtonSrc,
  googleButtonSrc,
}: HeroProps) {
  const primaryCtaHref = getDownloadLink();

  return (
    <section className="w-full max-w-6xl flex flex-col lg:flex-row items-stretch justify-between pt-20 pb-0 mb-0 px-10">
      <div className="max-w-lg lg:max-w-none lg:basis-3/5 lg:pr-12">
        <h1 className="text-4xl md:text-5xl font-bold leading-tight text-slate-800">
          {title}
        </h1>
        <p className="mt-6 text-slate-600 text-lg">{description}</p>

        <a href={primaryCtaHref} className="inline-block">
          <button className="mt-10 px-6 py-3 bg-violet-500 text-white rounded-full text-lg">
            {buttonText}
          </button>
        </a>

        <div className="flex gap-6 mt-20">
          <a
            href="https://play.google.com/store/apps/details?id=com.walrus.member"
            target="_blank"
            rel="noopener noreferrer"
            className="block w-32"
          >
            <img
              src={googleButtonSrc}
              alt="Google Play"
              className="w-full h-auto"
            />
          </a>
          <a
            // https://apps.apple.com/us/app/walrus-health/id1480276838?itscg=30200&itsct=apps_box_link&mttnsubad=1480276838
            href="https://apple.co/48hqxkB"
            target="_blank"
            rel="noopener noreferrer"
            className="block w-32"
          >
            <img
              src={appleButtonSrc}
              alt="App Store"
              className="w-full h-auto"
            />
          </a>
        </div>
      </div>

      <div className="mt-10 lg:mt-0 flex justify-center lg:justify-end mx-auto lg:mx-0 lg:basis-1/3 lg:min-w-[20%] lg:flex-shrink-0">
        <img
          src={imageSrc}
          alt={imageAlt}
          className="w-full max-w-xs lg:max-w-md object-contain"
        />
      </div>
    </section>
  );
}
